import * as THREE from 'three';
import { useEffect, useRef, useState } from 'react';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { Ori } from '../../assets/theme/js/variables';
import {
    addStudioLights,
    createSceneEnvironment,
    disposeSceneEnvironment,
    getViewportSize,
} from './renderables';

function useThreeViewport({
    onUserCameraInteraction,
}) {
    const mountRef = useRef(null);
    const sceneRef = useRef(null);
    const rendererRef = useRef(null);
    const cameraRef = useRef(null);
    const controlsRef = useRef(null);
    const stageRef = useRef(null);
    const resizeObserverRef = useRef(null);
    const frameIdRef = useRef(null);
    const environmentResourcesRef = useRef(null);
    const onUserCameraInteractionRef = useRef(onUserCameraInteraction);
    const [rendererDomElement, setRendererDomElement] = useState(null);

    useEffect(() => {
        onUserCameraInteractionRef.current = onUserCameraInteraction;
    }, [onUserCameraInteraction]);

    useEffect(() => {
        const mount = mountRef.current;

        if (!mount) {
            return undefined;
        }

        const { height, width } = getViewportSize(mount);

        sceneRef.current = new THREE.Scene();
        sceneRef.current.background = new THREE.Color(Ori.darkerGray);

        rendererRef.current = new THREE.WebGLRenderer({
            alpha: true,
            antialias: true,
            powerPreference: 'high-performance',
        });
        rendererRef.current.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        rendererRef.current.setSize(width, height);
        rendererRef.current.outputColorSpace = THREE.SRGBColorSpace;
        rendererRef.current.toneMapping = THREE.ACESFilmicToneMapping;
        rendererRef.current.toneMappingExposure = 1.2;

        mount.appendChild(rendererRef.current.domElement);
        setRendererDomElement(rendererRef.current.domElement);

        cameraRef.current = new THREE.PerspectiveCamera(1, width / height, 0.01, 5000);
        cameraRef.current.position.set(0, 0, 1);

        const headLight = new THREE.DirectionalLight(0xffffff, 0.45);
        headLight.position.set(0, 0, 1);
        headLight.target.position.set(0, 0, -1);
        cameraRef.current.add(headLight);
        cameraRef.current.add(headLight.target);
        sceneRef.current.add(cameraRef.current);

        controlsRef.current = new OrbitControls(cameraRef.current, rendererRef.current.domElement);
        controlsRef.current.enableDamping = true;
        controlsRef.current.dampingFactor = 0.08;
        controlsRef.current.rotateSpeed = 0.9;
        controlsRef.current.zoomSpeed = 1;
        controlsRef.current.panSpeed = 0.8;
        controlsRef.current.screenSpacePanning = true;
        controlsRef.current.target.set(0, 0, 0);
        controlsRef.current.mouseButtons.LEFT = THREE.MOUSE.ROTATE;
        controlsRef.current.mouseButtons.MIDDLE = THREE.MOUSE.DOLLY;
        controlsRef.current.mouseButtons.RIGHT = THREE.MOUSE.PAN;

        const handleControlsStart = () => {
            onUserCameraInteractionRef.current?.();
        };

        controlsRef.current.addEventListener('start', handleControlsStart);

        environmentResourcesRef.current = createSceneEnvironment(rendererRef.current);
        sceneRef.current.environment = environmentResourcesRef.current.sceneEnvironment.texture;

        stageRef.current = new THREE.Group();
        sceneRef.current.add(stageRef.current);
        addStudioLights(sceneRef.current);

        const handleResize = () => {
            if (!rendererRef.current || !cameraRef.current || !mountRef.current) {
                return;
            }

            const viewport = getViewportSize(mountRef.current);
            rendererRef.current.setSize(viewport.width, viewport.height);
            cameraRef.current.aspect = viewport.width / viewport.height;
            cameraRef.current.updateProjectionMatrix();
        };

        if (window.ResizeObserver) {
            resizeObserverRef.current = new window.ResizeObserver(handleResize);
            resizeObserverRef.current.observe(mount);
        } else {
            window.addEventListener('resize', handleResize);
        }

        const animate = () => {
            controlsRef.current?.update();

            if (rendererRef.current && sceneRef.current && cameraRef.current) {
                rendererRef.current.render(sceneRef.current, cameraRef.current);
            }

            frameIdRef.current = window.requestAnimationFrame(animate);
        };

        frameIdRef.current = window.requestAnimationFrame(animate);

        return () => {
            if (frameIdRef.current) {
                window.cancelAnimationFrame(frameIdRef.current);
                frameIdRef.current = null;
            }

            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
                resizeObserverRef.current = null;
            } else {
                window.removeEventListener('resize', handleResize);
            }

            controlsRef.current?.removeEventListener('start', handleControlsStart);
            controlsRef.current?.dispose();
            controlsRef.current = null;

            disposeSceneEnvironment(environmentResourcesRef.current);
            environmentResourcesRef.current = null;

            if (rendererRef.current) {
                rendererRef.current.domElement.style.cursor = '';
                rendererRef.current.dispose();

                if (mountRef.current && rendererRef.current.domElement.parentNode === mountRef.current) {
                    mountRef.current.removeChild(rendererRef.current.domElement);
                }

                rendererRef.current = null;
            }

            setRendererDomElement(null);
            sceneRef.current = null;
            cameraRef.current = null;
            stageRef.current = null;
        };
    }, []);

    return {
        cameraRef,
        controlsRef,
        mountRef,
        rendererDomElement,
        rendererRef,
        sceneRef,
        stageRef,
    };
}

export default useThreeViewport;
